/**
 * Web automation site form — CoordioCore form-page-pattern.
 *
 * Layout: PageFormLayout (ShellHeader + SectionSideNav grid + panel sections)
 * Fields: @coordio/ui FormField (label + ⓘ tooltip + error + required asterisk)
 * Cards:  @coordio/ui FormSection (title + description + bordered body)
 * Toggle: @coordio/ui FormChoiceField (label + Switch in horizontal row)
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Globe, Settings } from 'lucide-react';

import { CoordioDropdown, FormField, Input, Switch, Textarea } from '@coordio/ui';
import { FormSection } from '@coordio/ui/layout';

import { PageFormLayout } from '../layouts/PageFormLayout';
import { webAutomationService } from '../../services/webAutomationService';
import { useNotificationStore } from '../../store/notificationStore';
import { useContextStore } from '../../store/contextStore';

const AUTH_TYPES = [
  { value: 'None', label: 'None' },
  { value: 'BasicAuth', label: 'Username / Password' },
  { value: 'ApiKey', label: 'API Key' },
  { value: 'BearerToken', label: 'Bearer Token' },
  { value: 'OAuth2', label: 'OAuth 2.0' },
  { value: 'JWT', label: 'JWT' },
  { value: 'CustomHeader', label: 'Custom Header' },
];

export const WebAutomationSiteForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { addNotification } = useNotificationStore();
  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);
  const hasContext = Boolean(currentTenant && currentProject);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [authType, setAuthType] = useState('None');
  const [credentialJson, setCredentialJson] = useState('');
  const [hasStoredCredentials, setHasStoredCredentials] = useState(false);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (isEdit && id) {
      loadSite();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadSite = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await webAutomationService.getSite(id);
      setName(data.name);
      setUrl(data.url);
      setDescription(data.description || '');
      setAuthType(data.authType);
      setHasStoredCredentials(data.hasCredentials);
      setCredentialJson(''); // Never load stored credentials into the browser
      setIsActive(data.isActive);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load site';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const credentialHelperText = () => {
    switch (authType) {
      case 'BasicAuth':
        return 'JSON: { "username": "...", "password": "..." } — encrypted at rest, used by the desktop runner only';
      case 'ApiKey':
        return 'JSON: { "key": "...", "headerName": "..." } — encrypted at rest';
      case 'BearerToken':
        return 'JSON: { "token": "..." } — encrypted at rest';
      default:
        return 'Credential JSON — encrypted at rest, decrypted only for the desktop runner';
    }
  };

  const handleSubmit = async () => {
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!url.trim()) {
      setError('URL is required');
      return;
    }
    try {
      new URL(url.trim());
    } catch {
      setError('URL must be a valid URL');
      return;
    }
    if (credentialJson.trim()) {
      try {
        JSON.parse(credentialJson.trim());
      } catch {
        setError('Credentials must be valid JSON');
        return;
      }
    }

    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        url: url.trim(),
        description: description.trim() || undefined,
        authType,
        // On edit: undefined leaves stored credentials unchanged.
        credentialJson: credentialJson.trim() || undefined,
        isActive,
      };

      if (isEdit && id) {
        await webAutomationService.updateSite(id, data);
        addNotification('Site updated successfully', 'success');
      } else {
        await webAutomationService.createSite(data);
        addNotification('Site created successfully', 'success');
      }
      navigate('/web-automation/sites');
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to save site';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!hasContext) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-6 py-4 text-sm text-[var(--color-text-secondary)]">
          Please select a tenant and project first (using the switchers above) to create or edit a site.
        </div>
      </div>
    );
  }

  return (
    <PageFormLayout
      title={isEdit ? 'Edit Site' : 'Create Site'}
      subtitle="Configure a web automation target site with authentication credentials."
      data-testid="web-automation-site-form-page"
      loading={loading}
      error={error ?? undefined}
      onErrorDismiss={() => setError(null)}
      backTo={{ label: 'Back to List', href: '/web-automation/sites' }}
      actions={{
        onSave: handleSubmit,
        onCancel: () => navigate('/web-automation/sites'),
        saveLabel: isEdit ? 'Update' : 'Create',
        saving,
      }}
      navGroups={[
        {
          title: 'Profile',
          items: [
            { id: 'details', label: 'Details', icon: <Globe size={14} /> },
          ],
        },
        {
          title: 'Configuration',
          items: [
            { id: 'settings', label: 'Settings', icon: <Settings size={14} /> },
          ],
        },
      ]}
      sections={[
        {
          id: 'details',
          title: 'Details',
          description: 'Name, description, and URL for this automation site.',
          icon: <Globe size={16} />,
          content: (
            <FormSection
              title="Site identity"
              description="The name and URL identify this site for automation runners."
            >
              <div className="grid gap-5 items-end sm:grid-cols-2">
                <FormField
                  label="Name"
                  required
                  description="Display name for this automation site."
                  data-testid="web-automation-site-name"
                >
                  <Input
                    className="w-full"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="web-automation-site-name"
                  />
                </FormField>

                <FormField
                  label="URL"
                  required
                  description="The site the desktop runner will automate (e.g., https://portal.example.com)."
                  data-testid="web-automation-site-url"
                >
                  <Input
                    className="w-full"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    required
                    data-testid="web-automation-site-url"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField label="Description" description="Optional notes about this automation target.">
                  <Textarea
                    className="w-full"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                    data-testid="web-automation-site-description"
                  />
                </FormField>
              </div>
            </FormSection>
          ),
        },
        {
          id: 'settings',
          title: 'Settings',
          description: 'Authentication type, credentials, and active status.',
          icon: <Settings size={16} />,
          content: (
            <FormSection
              title="Site configuration"
              description="Choose an authentication method and set site-level options."
            >
              <FormField label="Authentication Type" description="How the runner authenticates with this site.">
                <CoordioDropdown
                  options={AUTH_TYPES}
                  value={authType}
                  onChange={(v) => setAuthType(v ?? 'None')}
                  clearable={false}
                  data-testid="web-automation-site-authType"
                />
              </FormField>

              {authType !== 'None' && (
                <div className="mt-5">
                  <FormField
                    label="Credentials"
                    description={
                      isEdit && hasStoredCredentials
                        ? `Credentials are stored (encrypted). Leave empty to keep them unchanged. ${credentialHelperText()}`
                        : credentialHelperText()
                    }
                  >
                    <Textarea
                      className="w-full"
                      value={credentialJson}
                      onChange={(e) => setCredentialJson(e.target.value)}
                      rows={4}
                      data-testid="web-automation-site-credentialJson"
                    />
                  </FormField>
                </div>
              )}

              <div className="mt-5">
                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch
                        checked={isActive}
                        onCheckedChange={setIsActive}
                        data-testid="web-automation-site-isActive"
                      />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Active</span>
                    </div>
                  </div>
                </div>
              </div>
            </FormSection>
          ),
        },
      ]}
    />
  );
};
